import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Tooltip from '@material-ui/core/Tooltip';
import {withStyles} from '@material-ui/core/styles';
import iconTotal from 'assets/customerInsights/ci-segment-total.svg';
import iconNew from 'assets/customerInsights/ci-segment-new.svg';
import iconOccasional from 'assets/customerInsights/ci-segment-occasional.svg';
import iconFrequent from 'assets/customerInsights/ci-segment-frequent.svg';
import iconInfo from 'assets/customerInsights/ci-icon-info.svg';
import {SEGMENT_DEFINITIONS} from '../constants';
import {CardsRow} from '../styles';

const InfoTooltip = withStyles({
  tooltip: {
    backgroundColor: '#ffffff',
    color: '#172751',
    fontFamily: "'Poppins', sans-serif",
    fontSize: 12,
    lineHeight: '150%',
    padding: '10px 12px',
    border: '1px solid #ff2d38',
    borderRadius: 8,
    maxWidth: 220,
    boxShadow: '0px 4px 12px 0px #00000022'
  },
  arrow: {
    color: '#ffffff',
    '&::before': {
      border: '1px solid #ff2d38',
      boxSizing: 'border-box'
    }
  }
})(Tooltip);

const Card = styled.div`
  background: #ffffff;
  border-radius: 10px;
  padding: 20px 20px 6px 22px;
  min-height: 131px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 19px;
  box-shadow: 0px 8px 16px 0px rgba(221, 221, 221, 0.32);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const IconImg = styled.img`
  width: 30px;
  height: 30px;
  display: block;
`;

const CardLabel = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 150%;
  color: #2f3d48;
`;

const InfoIcon = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  cursor: help;
  display: inline-flex;

  img {
    width: 28px;
    height: 28px;
    display: block;
  }
`;

const CardValue = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 42px;
  font-weight: 275;
  line-height: 150%;
  color: #2f3d48;
`;

const SEGMENT_ICONS = {
  totalCustomers: iconTotal,
  newCustomers: iconNew,
  occasionalCustomers: iconOccasional,
  frequentCustomers: iconFrequent
};

const formatValue = (n) =>
  n == null ? '—' : new Intl.NumberFormat('en-US').format(n);

const SegmentCard = ({segmentKey, value}) => {
  const {label, tooltip} = SEGMENT_DEFINITIONS[segmentKey];
  const icon = SEGMENT_ICONS[segmentKey];

  return (
    <Card>
      <CardHeader>
        <HeaderLeft>
          <IconImg src={icon} alt='' />
          <CardLabel>{label}</CardLabel>
        </HeaderLeft>
        <InfoTooltip
          title={tooltip}
          arrow
          placement='top-end'
          enterTouchDelay={0}
        >
          <InfoIcon
            type='button'
            aria-label={`${label} definition`}
            tabIndex={0}
          >
            <img src={iconInfo} alt='' />
          </InfoIcon>
        </InfoTooltip>
      </CardHeader>
      <CardValue>{formatValue(value)}</CardValue>
    </Card>
  );
};

SegmentCard.propTypes = {
  segmentKey: PropTypes.oneOf([
    'totalCustomers',
    'newCustomers',
    'occasionalCustomers',
    'frequentCustomers'
  ]).isRequired,
  value: PropTypes.number
};

SegmentCard.defaultProps = {
  value: null
};

const SegmentCards = ({segments}) => (
  <CardsRow>
    {Object.keys(SEGMENT_DEFINITIONS).map((key) => (
      <SegmentCard key={key} segmentKey={key} value={segments?.[key]} />
    ))}
  </CardsRow>
);

SegmentCards.propTypes = {
  segments: PropTypes.shape({
    totalCustomers: PropTypes.number,
    newCustomers: PropTypes.number,
    occasionalCustomers: PropTypes.number,
    frequentCustomers: PropTypes.number
  })
};

SegmentCards.defaultProps = {
  segments: null
};

export default SegmentCards;
